import React, { FC } from 'react';
import styled, { keyframes } from 'styled-components';
import theme from 'src/shared/theme';
import { T } from 'src/shared/helpers';
import { Icon } from 'src/components/widgets/icon';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';

/**
 * Clickable AI callout: the whole banner is the action, it announces an AI tool and opens it.
 * The title and the sparkles carry the AI Studio "A team of AI agents" purple gradient, animated
 * the same way; the surface stays a light lilac. Pass `onClose` to make it dismissible (adds an X).
 * Keep the gradient in sync with that accent.
 */

/** auto_awesome sparkle, filled and stroked with the moving AI Studio purple gradient. */
const GradientSparkles: FC = () => (
    <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none'>
        <g fill='url(#ai_banner_grad)' stroke='url(#ai_banner_grad)' strokeWidth='1.5' strokeLinejoin='round'>
            <polygon points='11.5,9.5 9,4 6.5,9.5 1,12 6.5,14.5 9,20 11.5,14.5 17,12' />
            <polygon points='19,9 20.25,6.25 23,5 20.25,3.75 19,1 17.75,3.75 15,5 17.75,6.25' />
            <polygon points='19,15 17.75,17.75 15,19 17.75,20.25 19,23 20.25,20.25 23,19 20.25,17.75' />
        </g>
        <defs>
            <linearGradient id='ai_banner_grad' x1='0' y1='0' x2='24' y2='0' gradientUnits='userSpaceOnUse' spreadMethod='reflect'>
                <stop offset='0' stopColor='#7c3aed' />
                <stop offset='0.5' stopColor='#a855f7' />
                <stop offset='1' stopColor='#ec4899' />
                <animateTransform attributeName='gradientTransform' type='translate' from='-24 0' to='24 0' dur='8s' repeatCount='indefinite' />
            </linearGradient>
        </defs>
    </svg>
);

/* Same animated purple gradient as the AI Studio "A team of AI agents" accent. */
const gradientShift = keyframes`
    0%   { background-position: 0% center; }
    50%  { background-position: 100% center; }
    100% { background-position: 0% center; }
`;

const Banner = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 7px 14px;
    margin: 0 0 16px;
    border: 1px solid #c4b5fd;
    border-radius: 8px;
    background: linear-gradient(120deg, #f9f6fe 0%, #f1e9fa 55%, #f7edf9 100%);
    transition: border-color 0.2s;
    flex-shrink: 0;

    &:hover {
        border-color: ${theme.ai.solid};
    }
`;

const ClickArea = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
    padding: 0;
    margin: 0;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    color: inherit;
    font: inherit;
`;

const BannerIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
    }
`;

const BannerText = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
    line-height: 1.35;
    color: ${theme.ai.calloutText};
`;

const BannerTitle = styled.span`
    font-size: 12px;
    font-weight: 600;
    background: linear-gradient(90deg, #7c3aed, #a855f7, #ec4899, #a855f7, #7c3aed);
    background-size: 300% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${gradientShift} 8s ease infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const BannerLead = styled.span`
    font-size: 11px;
    opacity: 0.85;
`;

const CloseButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 2px;
    margin: 0;
    border: none;
    background: none;
    cursor: pointer;
    color: ${theme.ai.calloutText};
    opacity: 0.6;
    transition: opacity 0.2s;

    &:hover {
        opacity: 1;
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;

interface AIGradientBannerProps {
    title: string;
    subtitle: string;
    onClick: () => void;
    /** When provided, renders a dismiss (X) button and calls this on click. */
    onClose?: () => void;
}

export const AIGradientBanner: FC<AIGradientBannerProps> = ({ title, subtitle, onClick, onClose }) => {
    return (
        <Banner>
            <ClickArea type='button' onClick={onClick}>
                <BannerIcon>
                    <GradientSparkles />
                </BannerIcon>
                <BannerText>
                    <BannerTitle>{title}</BannerTitle>
                    <BannerLead>{subtitle}</BannerLead>
                </BannerText>
            </ClickArea>
            {onClose && (
                <CloseButton type='button' onClick={onClose} aria-label={T._('Dismiss', 'Admin')}>
                    <Icon>
                        <CloseIcon />
                    </Icon>
                </CloseButton>
            )}
        </Banner>
    );
};
